import BackButton from '@/components/back-button';
import { DataTable } from '@/components/data-table';
import ContentLayout from '@/components/layout/ContentLayout';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTE } from '@/routes/constants';
import { getAssignmentStatsByTeacher, getStudentAssignmentStats } from '@/services/generatedAPIs';
import { QuestionStats } from '@/services/generatedAPIs.schemas';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown, Check, Clock3, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { isEN } from '@/utils/i18n';

type TableData = {
  id: number;
  name: string;
  classID?: number;
  initialCorrectRate: string;
  correctRate: string;
  submitRate: string;
  [key: `q${number}`]: string;
}

type ClassViewState = {
  assignmentId: number;
  classInfo: { id: number };
}

const ClassViewPageInner = ({ state }: { state: ClassViewState }) => {
  const { t, i18n } = useTranslation();
  const { getCurrentUser } = useAuth();
  let [tableData, setTableData] = React.useState<TableData[]>();
  let [columns, setColumns] = React.useState<ColumnDef<TableData>[]>([]);

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      return;
    }

    // Fetch both student assignment stats and assignment stats by teacher concurrently
    Promise.all([
      getStudentAssignmentStats({ assignmentId: state.assignmentId, classInfoId: state.classInfo.id }),
      getAssignmentStatsByTeacher({ teacherId: getCurrentUser()!.id })
    ]).then(([studentStatsRsp, teacherStatsRsp]) => {
      if (!tableData && studentStatsRsp.code === 200 && studentStatsRsp.data && studentStatsRsp.data.studentAssignmentStatList.length > 0) {
        const studentAssignmentStatList = studentStatsRsp.data.studentAssignmentStatList;
        // Build a map of studentId -> QuestionStats[] from the assignment stats
        // The QuestionStats contains isFirstAttemptCorrect for each student's question
        const studentQuestionStatsMap = new Map<number, QuestionStats[]>();
        if (teacherStatsRsp.code === 200 && teacherStatsRsp.data) {
          // Find the matching assignment in the teacher stats
          const matchingIndex = teacherStatsRsp.data.assignmentList.findIndex(
            (am) => am.assignment?.id === state.assignmentId && am.classInfo?.id === state.classInfo.id
          );

          if (matchingIndex >= 0) {
            const assignmentStatsV2 = teacherStatsRsp.data.assignmentStatsList[matchingIndex];
            if (assignmentStatsV2?.questionStats) {
              // Group QuestionStats by studentId
              for (const qs of assignmentStatsV2.questionStats) {
                if (qs.studentId != null) {
                  if (!studentQuestionStatsMap.has(qs.studentId)) {
                    studentQuestionStatsMap.set(qs.studentId, []);
                  }
                  studentQuestionStatsMap.get(qs.studentId)!.push(qs);
                }
              }
            }
          }
        }

        // Generate the question stats. 
        // Here, the assumption is that the data has been properly structured (i.e., for any student, the question stats are passed in identical order.
        const questionStats = studentAssignmentStatList[0].question.map((_, j) => {
          let submitted = 0;
          let correct = 0;
          for (const s of studentAssignmentStatList) {
            const q = s.question[j];
            if (q?.answer != null) {
              submitted++;
              if (q.isCorrectAnswer) correct++;
            }
          }
          const accuracy = submitted > 0 ? `${((correct / submitted) * 100).toFixed(1)}%` : '-';
          return { submitted, correct, accuracy };
        });

        const hasClassID = studentAssignmentStatList.filter((stat) => stat.student.studentClassNumber).length > 0;
        columns = [
          {
            id: "select",
            header: ({ table }) => (
              //   <Checkbox
              //     checked={
              //       table.getIsAllPageRowsSelected() ||
              //       (table.getIsSomePageRowsSelected() && "indeterminate")
              //     }
              //     onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              //     aria-label="Select all"
              //   />
              <></>
            ),
            cell: ({ row }) => (
              //   <Checkbox
              //     checked={row.getIsSelected()}
              //     onCheckedChange={(value) => row.toggleSelected(!!value)}
              //     aria-label="Select row"
              //   />
              <></>
            ),
            enableSorting: false,
            enableHiding: false,
          },
          {
            accessorKey: "name",
            header: ({ column }) => {
              return (
                <Button
                  variant="ghost"
                  className="h-auto font-normal justify-start hover:bg-transparent"
                  style={{ padding: 0 }}
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                  {t("user.name")}
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              )
            },
            cell: ({ row }) => (
              <div>{row.original.name}</div>
            ),
          },
        ]

        if (hasClassID) {
          columns.push({
            accessorKey: "classID",
            header: ({ column }) => {
              return (
                <Button
                  variant="ghost"
                  className="h-auto font-normal justify-start hover:bg-transparent"
                  style={{ padding: 0 }}
                  onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                  {t("common.classID")}
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              )
            },
            cell: ({ row }) => (
              <div>{row.original.classID}</div>
            ),
          })
        }

        // 添加首试答对率列 (Initial Correct Rate)
        columns.push({
          accessorKey: "initialCorrectRate",
          header: ({ column }) => {
            return (
              <Button
                variant="ghost"
                className="h-auto font-normal justify-start hover:bg-transparent"
                style={{ padding: 0 }}
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                <div className="flex flex-col leading-tight items-start">
                  <span className="flex items-center">
                    {t("teacher.correctRate")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </span>
                  <span className="text-xs text-muted-foreground">{t("teacher.initialAttempt")}</span>
                </div>
              </Button>
            )
          },
          cell: ({ row }) => (
            <div>{row.original.initialCorrectRate}</div>
          ),
        });

        // 添加最终答对率列 (Latest Correct Rate)
        columns.push({
          accessorKey: "correctRate",
          header: ({ column }) => {
            return (
              <Button
                variant="ghost"
                className="h-auto font-normal justify-start hover:bg-transparent"
                style={{ padding: 0 }}
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                <div className="flex flex-col leading-tight items-start">
                  <span className="flex items-center">
                    {t("teacher.correctRate")}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </span>
                  <span className="text-xs text-muted-foreground">{t("teacher.latestAttempt")}</span>
                </div>
              </Button>
            )
          },
          cell: ({ row }) => (
            <div>{row.original.correctRate}</div>
          ),
        });

        // 添加提交率列
        columns.push({
          accessorKey: "submitRate",
          header: ({ column }) => {
            return (
              <Button
                variant="ghost"
                className="h-auto font-normal justify-start hover:bg-transparent"
                style={{ padding: 0 }}
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                {t("teacher.submitRate")}
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            )
          },
          cell: ({ row }) => (
            <div>{row.original.submitRate}</div>
          ),
        });

        for (let j = 0; j < studentAssignmentStatList[0].question.length; j++) {
          let displayIndex = j + 1;
          columns.push({
            accessorKey: `q${j}`,
            header: () => (
              <div className="flex flex-col leading-tight">
                <span>{t("common.question") + displayIndex}</span>
                <span className="text-xs text-muted-foreground">
                  {t("teacher.question-accuracy")}:
                  {questionStats[j].submitted > 0
                    ? ` ${questionStats[j].accuracy}`
                    : " N/A"}
                </span>
              </div>
            ),
            cell: ({ row }) => {
              const info = row.original;
              const stat = info[`q${j}`];
              if (stat === "correct") {
                return (<Check color='blue'></Check>);
              } else if (stat === "incorrect") {
                return (<X color='red'></X>);
              } else if (stat === "not submitted") {
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Clock3></Clock3>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("teacher.notsubmit")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              } else {
                return <div>{row.original[`q${j}`]}</div>
              }
            },
          });
        }

        columns.push(
          {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => {
              const info = row.original;

              return (
                <div className='flex place-content-evenly'>
                  <NavLink to={ROUTE.TEACHER.HOMEWORK_STUDENTVIEW} state={{ assignmentId: state.assignmentId, studentId: info.id }}><Button variant="outline">{t("teacher.studentview")}</Button></NavLink>
                </div>
              )
            },
          },
        );
        setColumns(columns);

        tableData = [];
        for (let i = 0; i < studentAssignmentStatList.length; i++) {
          const questions = studentAssignmentStatList[i].question;
          const studentId = studentAssignmentStatList[i].student.id!;

          // 计算答对率和提交率
          let correctCount = 0;
          let submittedCount = 0;
          const totalCount = questions.length;

          for (let j = 0; j < questions.length; j++) {
            const question = questions[j];
            if (question.answer) {
              submittedCount++;
              if (question.isCorrectAnswer) {
                correctCount++;
              }
            }
          }

          const correctRate = totalCount > 0 ? ((correctCount / totalCount) * 100).toFixed(1) + '%' : '0%';
          const submitRate = totalCount > 0 ? ((submittedCount / totalCount) * 100).toFixed(1) + '%' : '0%';

          // 计算首试答对率 (Initial Correct Rate based on isFirstAttemptCorrect)
          let initialCorrectCount = 0;
          const studentQStats = studentQuestionStatsMap.get(studentId);
          if (studentQStats && studentQStats.length > 0) {
            for (const qs of studentQStats) {
              if (qs.isFirstAttemptCorrect === true) {
                initialCorrectCount++;
              }
            }
          }
          const initialCorrectRate = totalCount > 0 ? ((initialCorrectCount / totalCount) * 100).toFixed(1) + '%' : '-';

          let data: TableData = {
            id: studentId,
            name: isEN(i18n) ? (studentAssignmentStatList[i].student.nameEn || studentAssignmentStatList[i].student.name)! : studentAssignmentStatList[i].student.name!,
            classID: studentAssignmentStatList[i].student.studentClassNumber,
            initialCorrectRate: initialCorrectRate,
            correctRate: correctRate,
            submitRate: submitRate
          };

          for (let j = 0; j < questions.length; j++) {
            const question = questions[j];
            data[`q${j}`] = (question.answer ? (question.isCorrectAnswer ? "correct" : "incorrect") : "not submitted");
          }
          tableData.push(data);
        }
        setTableData(tableData)
      }
    }
    ).catch((e) => {
      console.error("Error fetching assignment stats", e);
    }
    );
  });

  return (
    <ContentLayout title={t("teacher.classview")}>
      <div className="mx-auto space-y-6">
        <div className="bg-white shadow-md rounded-lg m-2 p-6">
          <div className="mb-4">
            <BackButton />
          </div>
          <DataTable data={tableData ?? []} columns={columns} filterColumn='name' filterName={t('user.name')} />
        </div>
      </div>
    </ContentLayout>
  );
};

const ClassViewPage = () => {
  const state = useLocation().state as ClassViewState | null;
  const navigate = useNavigate();

  const isValidState = (s: any): s is ClassViewState => {
    return !!s && typeof s.assignmentId === 'number' && !!s.classInfo && typeof s.classInfo.id === 'number';
  };

  React.useEffect(() => {
    if (!isValidState(state)) {
      navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
    }
  }, [state, navigate]);

  if (!isValidState(state)) {
    return <LoadingOverlay />;
  }

  return <ClassViewPageInner state={state} />;
};

export default ClassViewPage;